import type { ExtensionInstallationSummary } from "@api-client";
import type { ExtensionCatalogPackage } from "@/lib/api";

export type ExtensionCatalogVersion = ExtensionCatalogPackage["versions"][number];

export interface ExtensionUpdate {
  installation: ExtensionInstallationSummary;
  package: ExtensionCatalogPackage;
  latest: ExtensionCatalogVersion;
}

interface ParsedVersion {
  core: [number, number, number];
  prerelease: string[] | null;
}

function parseVersion(value: string): ParsedVersion | null {
  const match = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/.exec(value);
  if (!match) return null;
  return {
    core: [Number(match[1]), Number(match[2]), Number(match[3])],
    prerelease: match[4] ? match[4].split(".") : null,
  };
}

function comparePrerelease(left: string[], right: string[]): number {
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (a === undefined) return -1;
    if (b === undefined) return 1;
    const aNumeric = /^\d+$/.test(a);
    const bNumeric = /^\d+$/.test(b);
    if (aNumeric && bNumeric) {
      const difference = Number(a) - Number(b);
      if (difference !== 0) return difference;
    } else if (aNumeric !== bNumeric) {
      return aNumeric ? -1 : 1;
    } else if (a !== b) {
      return a < b ? -1 : 1;
    }
  }
  return 0;
}

/** Semver order for the canonical numeric versions used by extensions. */
export function compareExtensionVersions(left: string, right: string): number {
  const a = parseVersion(left);
  const b = parseVersion(right);
  if (!a || !b) return 0;
  for (let index = 0; index < 3; index += 1) {
    if (a.core[index] !== b.core[index]) return a.core[index] - b.core[index];
  }
  if (!a.prerelease && !b.prerelease) return 0;
  if (!a.prerelease) return 1;
  if (!b.prerelease) return -1;
  return comparePrerelease(a.prerelease, b.prerelease);
}

export function latestCatalogVersion(
  catalogPackage: ExtensionCatalogPackage,
): ExtensionCatalogVersion | null {
  return catalogPackage.versions.reduce<ExtensionCatalogVersion | null>(
    (latest, candidate) =>
      latest && compareExtensionVersions(candidate.version, latest.version) <= 0
        ? latest
        : candidate,
    null,
  );
}

/** Stable releases only; prereleases are opt-in through the catalog. */
export function latestStableCatalogVersion(
  catalogPackage: ExtensionCatalogPackage,
): ExtensionCatalogVersion | null {
  return catalogPackage.versions.reduce<ExtensionCatalogVersion | null>(
    (latest, candidate) => {
      const parsed = parseVersion(candidate.version);
      if (!parsed || parsed.prerelease) return latest;
      return latest && compareExtensionVersions(candidate.version, latest.version) <= 0
        ? latest
        : candidate;
    },
    null,
  );
}

/** Installed extensions whose catalog package has a strictly newer release. */
export function findExtensionUpdates(
  installations: ExtensionInstallationSummary[],
  catalog: ExtensionCatalogPackage[],
): ExtensionUpdate[] {
  const packages = new Map(catalog.map((item) => [item.id, item]));
  const updates: ExtensionUpdate[] = [];
  for (const installation of installations) {
    if (installation.state === "uninstalled") continue;
    const catalogPackage = packages.get(installation.package_id);
    if (!catalogPackage) continue;
    const latest = latestStableCatalogVersion(catalogPackage);
    if (!latest) continue;
    if (compareExtensionVersions(latest.version, installation.version) <= 0) continue;
    updates.push({ installation, package: catalogPackage, latest });
  }
  return updates;
}
